// src/pages/JobBoardAdminPage.js — staff view of the Job Board: every post, delete any
// [marker: finish-up v1]
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../utils/api';
import toast from 'react-hot-toast';
import DataGrid from '../components/DataGrid';
import { JOB_TYPE_LABELS, WORKPLACE_LABELS } from './PublicJobsPage';

export default function JobBoardAdminPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewing, setViewing] = useState(null);
  const load = () => {
    setLoading(true);
    api.get('/jobs-admin').then(r => setRows(r.data || [])).catch(() => toast.error('Could not load the job board.')).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);
  const remove = async (p) => {
    if (!window.confirm(`Delete “${p.title}” (${p.company_name || p.business_name})? It comes off the job board right away.`)) return;
    try { await api.delete(`/jobs-admin/${p.id}`); toast.success('Deleted.'); setViewing(null); load(); }
    catch (err) { toast.error(err.response?.data?.error || 'Could not delete.'); }
  };
  const publicUrl = `${window.location.origin}/jobs`;
  const copy = (t) => { try { navigator.clipboard.writeText(t); toast.success('Copied.'); } catch { toast.error('Could not copy.'); } };
  const columns = [
    { key: 'title', label: 'Job', width: 230, render: p => <strong>{p.title}</strong> },
    { key: 'company_name', label: 'Company', width: 200 },
    { key: 'business_name', label: 'Member', width: 200, render: p => (p.business_id ? <Link to={`/members/${p.business_id}`} onClick={e => e.stopPropagation()}>{p.business_name}</Link> : '—') },
    { key: 'posted_by', label: 'Posted By', width: 150 },
    { key: 'location', label: 'Location', width: 140 },
    { key: 'job_type', label: 'Type', width: 100, text: p => JOB_TYPE_LABELS[p.job_type] || '' },
    { key: 'workplace', label: 'Where', width: 90, text: p => WORKPLACE_LABELS[p.workplace] || '' },
    { key: 'posted', label: 'Posted', width: 105, type: 'date' },
    { key: 'actions', label: '', width: 90, render: p => <button className="btn btn-ghost btn-sm" style={{ color: 'var(--danger)' }} onClick={e => { e.stopPropagation(); remove(p); }}>Delete</button> },
  ];
  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Job Board</h1>
          <p className="page-subtitle">Jobs posted by members from the member portal. Anyone can see the board; staff can delete any post.</p>
        </div>
        <a className="btn btn-secondary" href="/jobs" target="_blank" rel="noreferrer">Open the public board ↗</a>
      </div>
      <div className="card" style={{ padding: '12px 16px', marginBottom: 14, fontSize: '0.875rem' }}>
        <div style={{ fontWeight: 700, marginBottom: 6 }}>Links for the website</div>
        <div style={{ display: 'grid', gap: 6 }}>
          <LinkRow label="Job board page (link or embed this)" url={publicUrl} onCopy={copy} />
          <LinkRow label="Embed version (no header, for inside a Wix page)" url={`${window.location.origin}/embed/jobs`} onCopy={copy} />
          <LinkRow label="Member login (portal)" url={`${window.location.origin}/portal/login`} onCopy={copy} />
        </div>
      </div>
      <DataGrid gridKey="job-board" rows={rows} loading={loading} columns={columns} onRowClick={p => setViewing(p)} emptyText="No jobs posted yet." />
      {viewing && (
        <div className="modal-overlay" onClick={() => setViewing(null)}>
          <div className="modal modal-lg" onClick={e => e.stopPropagation()} style={{ maxWidth: 720 }}>
            <div className="modal-header">
              <span className="modal-title">{viewing.title}</span>
              <button className="btn btn-ghost btn-icon" onClick={() => setViewing(null)}>✕</button>
            </div>
            <div className="modal-body" style={{ maxHeight: '70vh', overflow: 'auto' }}>
              <div style={{ color: 'var(--gray-600)', marginBottom: 10 }}>
                {viewing.company_name}{viewing.location ? ` · ${viewing.location}` : ''}{viewing.salary ? ` · ${viewing.salary}` : ''}
                <div style={{ fontSize: '0.8rem' }}>Posted {viewing.posted} by {viewing.posted_by || 'a member'}{viewing.posted_by_email ? ` (${viewing.posted_by_email})` : ''}</div>
              </div>
              <div style={{ whiteSpace: 'pre-wrap', lineHeight: 1.55 }}>{viewing.description}</div>
              <div style={{ marginTop: 12, fontSize: '0.875rem' }}>
                <strong>How to apply:</strong> {[viewing.apply_email, viewing.apply_url, viewing.how_to_apply].filter(Boolean).join(' · ')}
              </div>
            </div>
            <div className="modal-footer" style={{ justifyContent: 'space-between' }}>
              <button className="btn btn-danger" onClick={() => remove(viewing)}>Delete post</button>
              <a className="btn btn-secondary" href={`/jobs?job=${viewing.id}`} target="_blank" rel="noreferrer">See it on the board ↗</a>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
const LinkRow = ({ label, url, onCopy }) => (
  <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
    <span style={{ minWidth: 300, color: 'var(--gray-600)' }}>{label}</span>
    <code style={{ background: 'var(--gray-50)', padding: '3px 8px', borderRadius: 4 }}>{url}</code>
    <button className="btn btn-ghost btn-sm" onClick={() => onCopy(url)}>Copy</button>
  </div>
);
